import { PermissionKey, hasPermissionV2 } from '@c4ytechnology/authz';
import type { AdminConsoleUser, AdminRole } from '../api/adminConsole';
import { rememberedApplicationsListPath } from './applicationsListFilters';

// RBAC Phase 1 (catalog v2). Decisions come from `GET /auth/me` → `access.permissions`, which
// admin-api resolves from role assignments (v2) or translates from groups (v1), so the SPA makes
// the same decision in both modes. There is no role bypass: Super Admin is an ordinary role that
// holds every key. A session without `access` holds nothing and is denied everywhere.

/** The part of the admin-api session the permission checks read. */
export type AdminAccessHolder = Pick<AdminConsoleUser, 'access'> | null | undefined;

export function hasAdminPermission(user: AdminAccessHolder, key: PermissionKey): boolean {
  return hasPermissionV2(user?.access, key);
}

/** Route prefix → the v2 read key that gates it (and every nested path under it). */
export const TAB_PERMISSION: Readonly<Record<string, PermissionKey>> = {
  '/applications': PermissionKey.APPLICATIONS_READ,
  '/doctrine-config': PermissionKey.DOCTRINE_READ,
  '/providers': PermissionKey.PROVIDERS_READ,
  '/routing': PermissionKey.ROUTING_READ,
  '/service-onboarding': PermissionKey.SERVICE_ONBOARDING_READ,
  '/vendors': PermissionKey.VENDORS_READ,
  '/income-verification': PermissionKey.INCOME_VERIFICATION_READ,
  '/employer-verification': PermissionKey.EMPLOYER_VERIFICATION_READ,
  '/identity-verification': PermissionKey.IDENTITY_VERIFICATION_READ,
  // `/admin/users` also gates the user detail page `/admin/users/:id`.
  '/admin/users': PermissionKey.IAM_USERS_READ,
  '/admin/groups': PermissionKey.IAM_ROLES_READ,
  '/admin/roles': PermissionKey.IAM_ROLES_READ,
  // RBAC Phase 2. Actions on these pages check their own write/manage/decide keys.
  '/admin/access/expiring': PermissionKey.IAM_USERS_READ,
  '/admin/access/reviews': PermissionKey.IAM_REVIEWS_READ,
  // RBAC Phase 4: eligibility admin; create/edit/remove check iam.assignments.write.
  '/admin/access/eligibilities': PermissionKey.IAM_USERS_READ,
  '/admin/audit-log': PermissionKey.ADMIN_AUDIT_READ,
};

/** In-app pages any signed-in operator may open. */
// RBAC Phase 4: My access and the approvals inbox are for every staff member — admin-api decides
// who is eligible or may approve — including one whose critical role is eligible-only.
export const PUBLIC_IN_APP_PATHS: readonly string[] = ['/no-access', '/my-access', '/my-access/approvals'];

/**
 * Routes that render only a `<Navigate>` (retired tabs and legacy aliases). They are let through
 * so the stub can redirect; the destination is then gated on its own. Blocking them instead would
 * race the stub's redirect (e.g. `/doctrines/:id` → `/doctrine-config/:id`).
 */
export const REDIRECT_STUB_PATHS: readonly string[] = [
  '/',
  '/dashboard',
  '/doctrines',
  '/audit',
  '/admin/audit',
  '/mocking',
  '/service-configs',
  '/income-verification-dashboard',
  '/employer-verification-dashboard',
  '/identity-verification-dashboard',
];

/** Stub prefixes whose nested paths are also pure redirects. */
const REDIRECT_STUB_PREFIXES: readonly string[] = ['/doctrines', '/service-configs'];

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function isRedirectStub(pathname: string): boolean {
  return (
    REDIRECT_STUB_PATHS.includes(pathname) ||
    REDIRECT_STUB_PREFIXES.some((prefix) => pathname.startsWith(`${prefix}/`))
  );
}

/** The read key gating `pathname`, or undefined when the path is not in the route map. */
export function requiredPermissionForPath(pathname: string): PermissionKey | undefined {
  const match = Object.keys(TAB_PERMISSION)
    .sort((a, b) => b.length - a.length)
    .find((prefix) => matchesPrefix(pathname, prefix));
  return match ? TAB_PERMISSION[match] : undefined;
}

/**
 * Deny by default: a path is allowed only when it is mapped and the user holds its key, or it is
 * an explicit public page / redirect stub and the user is signed in.
 */
export function pathAllowed(pathname: string, user: AdminAccessHolder): boolean {
  if (!user) return false;
  const required = requiredPermissionForPath(pathname);
  if (required) return hasAdminPermission(user, required);
  if (PUBLIC_IN_APP_PATHS.includes(pathname) || isRedirectStub(pathname)) return true;
  return false;
}

/** Landing order when the requested path is not allowed. */
export const LANDING_ORDER: readonly string[] = [
  '/applications',
  '/doctrine-config',
  '/providers',
  '/routing',
  '/service-onboarding',
  '/vendors',
  '/income-verification',
  '/employer-verification',
  '/identity-verification',
  '/admin/users',
  '/admin/roles',
  '/admin/access/expiring',
  '/admin/access/reviews',
  '/admin/access/eligibilities',
  '/admin/audit-log',
  '/admin/groups',
];

export function firstAllowedPath(user: AdminAccessHolder): string {
  const found = LANDING_ORDER.find((path) => pathAllowed(path, user)) ?? '/no-access';
  return found === '/applications' ? rememberedApplicationsListPath() : found;
}

export function navVisible(to: string, user: AdminAccessHolder): boolean {
  return pathAllowed(to, user);
}

export type { AdminRole };
